import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { Option } from 'app/types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../redux/slice';
import { selectCustomers, selectIsLoading, selectPagination } from '../redux/selector';
import { selectOrganization } from 'app/redux/selectors';
import { selectDirection } from 'styles/theme/slice';
import { useSearchParams } from 'react-router-dom';

interface SelectInputProps {
    readonly value?: Option;
    readonly onChange?: (newValue?: Option | null) => void;
    readonly isLoading?: boolean;
    readonly disabled?: boolean;
    readonly error?: string;
    readonly backgroundColor?: string;
    readonly borderWidth?: number;
    readonly defaultValue?: Option;
    readonly showAll?: boolean
}

export default function SelectInput({
    value,
    onChange,
    isLoading,
    error,
    disabled,
    backgroundColor,
    borderWidth,
    defaultValue,
    showAll = false
}: SelectInputProps) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const pagination = useSelector(selectPagination);
    const loading = useSelector(selectIsLoading)
    const customers = useSelector(selectCustomers);
    const organization = useSelector(selectOrganization);
    const { t } = useTranslation();
    const direction = useSelector(selectDirection);
    const dispatch = useDispatch();

    const [searchParams] = useSearchParams();
    const  customerId= searchParams.get("customerId");

    useEffect(() => {
        if (customerId) {
            dispatch(actions.getCustomersSearch({
                page: pagination.page,
                perPage: pagination.perPage,
                search: customerId,
                isActive: pagination.filters?.isActive,
                organization: organization?.value,
                callback: (v) => {
                    if(v){
                        onChange && onChange(v)
                    }
                  
                }
            }));
        }
    }, [dispatch, customerId]);

    useEffect(() => {
        if (open) {
            dispatch(actions.getCustomers({
                page: pagination.page,
                perPage: pagination.perPage,
                search: inputValue,
                isActive: pagination.filters?.isActive,
                organization: organization?.value,
            }));
        }
    }, [dispatch, open, pagination.page, pagination.perPage, pagination.filters?.isActive, inputValue, organization?.value]);

    const handleScroll = (event: React.SyntheticEvent) => {
        const hasMore = pagination.page < pagination.totalPage;
        if (!hasMore || isLoading) return;

        const listboxNode = event.currentTarget;
         const bottom = Math.ceil(listboxNode.scrollTop + listboxNode.clientHeight) >= listboxNode.scrollHeight 
     
      if (bottom) {
            dispatch(actions.getCustomers({
                page: pagination.page + 1,
                perPage: pagination.perPage,
                search: inputValue,
                isActive: pagination.filters?.isActive,
                organization: organization?.value,
            }));
        }
    };

    const options = customers?.map((c) => ({
        label: c.name ?   `${c.name} / ${c.email}` :  `${c.email}`,
        value: c._id ?? '',
    })) || [];

    return (
        <Autocomplete
            sx={{
                width: "100%",
            }}
            open={open}
            onOpen={() => {
                if (!disabled) {
                    setOpen(true);
                }
            }}
            onClose={() => {
                setOpen(false);
            }}
            value={value}
            defaultValue={defaultValue}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            getOptionLabel={(option) => option?.label ?? ''}
            onChange={(e, v) => onChange?.(v)}
            options={showAll ? [
                {
                    label: t('COMMON.TEXT.ALL'),
                    value: 'all',
                },
                ...options,
            ]: options}
            loading={loading || isLoading}
            disabled={disabled}
            loadingText={
                <Typography
                    sx={{
                        
                        fontSize: '16px',
                        fontWeight: 400,
                        lineHeight: '14px',
                        textAlign: 'left',
                    }}
                >
                    {t("COMMON.TEXT.LOADING")}
                </Typography>
            }
            noOptionsText={
                <Typography
                    sx={{
                        
                        fontSize: '16px',
                        fontWeight: 400,
                        lineHeight: '14px',
                        textAlign: 'left',
                    }}
                >
                    {t("COMMON.TEXT.NO_OPTIONS")}
                </Typography>
            }
            ListboxProps={{
                onScroll: handleScroll,
            }}
            renderOption={(props, option) => (
                <Box
                    component="li"
                    {...props}
                >
                    <Typography
                        sx={{
                            
                            fontSize: '16px',
                            fontWeight: 400,
                            lineHeight: '14px',
                            color: '#5E6781',
                            py: '4px',
                        }}
                    >
                        {option?.label}
                    </Typography>
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={t("COMMON.PLACEHOLDERS.SELECT")}
                    disabled={disabled}
                    sx={{
                        width: "100%",
                        backgroundColor: backgroundColor ?? '#FFFFFF',
                        borderRadius: '4px',
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: error ? '#d32f2f' : '#86BDE3',
                                borderRadius: '4px',
                                borderWidth: borderWidth ?? 1,
                            },
                            '&:hover fieldset': {
                                borderColor: error ? '#d32f2f' : '#86BDE3',
                                borderRadius: '4px',
                                borderWidth: borderWidth ?? 1,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: error ? '#d32f2f' : '#86BDE3',
                                borderRadius: '4px',
                                borderWidth: borderWidth ?? 1,
                            },
                        },
                        '& .MuiFormHelperText-root': {
                            
                            fontSize: "12px",
                            fontWeight: 500,
                            color: '#d32f2f',
                        },
                        input: {
                            '&::placeholder': {
                                color: '#727F89',
                                fontSize: '14px',
                                fontWeight: 400,
                            },
                            fontWeight: 400,
                            fontSize: '14px',
                            color: '#263238',
                            backgroundColor: backgroundColor ?? '#FFFFFF',
                        },
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {(loading || isLoading) ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    helperText={error}
                />
            )}
        />
    );
}
